import { useTranslation } from "react-i18next";

import { useAttributesContext } from "shared/contexts/AttributesContext";
import { ContextProviderName } from "shared/contexts/types";
import {
  SIGNAL_EVENT_OCCURRENCES_GENERIC_FILTER,
  SIGNAL_EVENTS_GENERIC_FILTER,
  VEHICLES_GENERIC_FILTER,
} from "shared/filterDefinitions";
import { VEHICLE_ACCESSOR } from "shared/schemas/constants";
import useGeneralDescriptionSchema from "shared/schemas/generalDescriptionSchema";
import {
  SchemaOverride,
  SchemaRelations,
  UseSchema,
} from "shared/schemas/types";
import { getGroupBySelectOptions } from "shared/schemas/utils/group-by-utils";
import {
  getAttributeMap,
  getDisplayLabelFunction,
  getFilterFunction,
  getSchemaEntryFunction,
  getSchemaForAccessorFunction,
  getSchemaFromAttributes,
} from "shared/schemas/utils/schema-utils";
import useVehiclesSchema from "shared/schemas/vehiclesSchema";

import { SchemaEntry } from "features/ui/MaterialTable";

import { mergeSchemas } from "./utils";

const DEFAULT_FIELDS = [
  "signalEventID",
  "VIN",
  "recordedAt",
  "vehicle.vehicleManufacturedAt",
  "odometer",
  "crewCollection",
  "crewRepair",
  "dtcConfirmed",
  "evaluationComment",
];

const useSignalEventOccurrencesViewSchema = (fields?: string[]): UseSchema => {
  const { t } = useTranslation();
  const vehiclesSchema = useVehiclesSchema();

  const { attributes } = useAttributesContext(
    ContextProviderName.SignalEventOccurrence
  );

  const generalDescriptionSchema = useGeneralDescriptionSchema();

  const attributeMap = getAttributeMap(attributes);

  const getDisplayLabel = getDisplayLabelFunction(attributeMap);

  const getFilter = getFilterFunction(
    SIGNAL_EVENT_OCCURRENCES_GENERIC_FILTER,
    attributeMap
  );

  const getSchemaEntry = getSchemaEntryFunction(getFilter, attributeMap);

  const overrides: Record<string, SchemaOverride> = {
    // Do not allow filtering on "Vehicle" via UI. We use a separate query param for that.
    [VEHICLE_ACCESSOR]: {
      schemaOverride: {
        filter: undefined,
      },
    },
    signalEventID: {
      schemaOverride: {
        filter: SIGNAL_EVENTS_GENERIC_FILTER({
          fieldName: "signalEventID",
          fieldNameForAPI: "ID",
          label: getDisplayLabel(
            "signalEventID",
            t("schema:dashboard.signal_event_id")
          ),
          search: true,
          disableFiltering: true,
          disableContainsFilters: true,
        }),
      },
    },
    repairComment: {
      schemaOverride: { limitedWidthClass: "w-8" },
    },
    evaluationComment: {
      schemaOverride: { limitedWidthClass: "w-8" },
    },
    VIN: {
      schemaOverride: {
        filter: VEHICLES_GENERIC_FILTER({
          fieldName: "VIN",
          label: getDisplayLabel("VIN", t("common:vin")),
          search: true,
          disableFiltering: true,
        }),
      },
    },
  };

  const relations: SchemaRelations = {
    "/api/v1/parts": generalDescriptionSchema,
  };

  const schema: SchemaEntry<string>[] = getSchemaFromAttributes(
    attributes,
    getSchemaEntry,
    overrides,
    relations,
    []
  );

  const outFields =
    fields != null && fields.length > 0 ? fields : DEFAULT_FIELDS;

  const outSchema = mergeSchemas(outFields, schema, vehiclesSchema.schema);

  const getSchemaForAccessor = getSchemaForAccessorFunction(schema);

  const groupByRelations: SchemaRelations = {
    "/api/v1/vehicles": vehiclesSchema,
  };

  const groupBySelectOptions = getGroupBySelectOptions(
    attributes,
    groupByRelations
  );

  return {
    schema: outSchema,
    getDisplayLabel,
    attributeMap,
    attributes,
    groupBySelectOptions,
    attributeAccessors: schema.map(
      ({ accessor, filter }) => filter?.fieldName || accessor
    ),
    getSchemaForAccessor,
    getSchemaEntry,
  };
};
export default useSignalEventOccurrencesViewSchema;
